import { Menu, ChevronLeft, HelpCircle, Search, Bell, Loader2, HeartPulse, HeartCrack, LifeBuoy, ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';


import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHealthStatus } from '../services/useHealthStatus';
import { getTokenDiagnostics, useAuth } from '../context/AuthContext';
import './Header.css'

const logoSrc = new URL('../Logo.png', import.meta.url).href

interface HeaderProps {
  onMenuToggle: () => void;
  onProfileClick: () => void;
  onHelpClick: () => void;
  onSupportClick: () => void;
  isSidebarCollapsed: boolean;
  notificationCount?: number;
  processCount?: number;
  userName?: string;
  userCompany?: string;
  userAvatar?: string;
}

export default function Header({
  onMenuToggle,
  onProfileClick,
  onHelpClick,
  onSupportClick,
  isSidebarCollapsed,
  notificationCount = 0,
  processCount = 0,
  userName = 'Guest User',
  userCompany = 'Organization',
  userAvatar
}: HeaderProps) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchScope, setSearchScope] = useState<'products' | 'distributors'>('products')
  const [showHealthDetails, setShowHealthDetails] = useState(false)
  const { dbHealthy, apiHealthy, checkedAt } = useHealthStatus()
  const { token, user, isAuthenticated } = useAuth()
  const tokenDiagnostics = getTokenDiagnostics(token)

  const authMethod = user?.authMethod ?? 'unknown'
  const authStatus: 'green' | 'amber' | 'red' =
    !isAuthenticated || !tokenDiagnostics.valid
      ? 'red'
      : authMethod === 'sso'
        ? 'green'
        : 'amber'

  const authTitle =
    authStatus === 'green'
      ? 'SSO authentication active'
      : authStatus === 'amber'
        ? 'Local authentication active'
        : 'Authentication token missing/invalid'

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const query = searchQuery.trim()
    if (!query) return

    const params = new URLSearchParams({ search: query })
    if (searchScope === 'distributors') {
      navigate(`/distributors?${params.toString()}`)
    } else {
      navigate(`/products?${params.toString()}`)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <header className="app-header">
      <div className="header-left">
        <button 
          className="menu-toggle"
          onClick={onMenuToggle}
          aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isSidebarCollapsed ? <Menu size={24} /> : <ChevronLeft size={24} />}
        </button>
        
        <div className="header-brand">
          <img src={logoSrc} alt="MIM GRC" className="header-logo" />
          <div className="header-title">
            <span className="app-name">MIM GRC</span>
            <span className="app-tagline">Compliance Platform</span>
          </div>
        </div>
      </div>

      <div className="header-center">
        <form className="search-form" onSubmit={handleSearch}>
          <select
            className="search-scope"
            value={searchScope}
            onChange={(e) => setSearchScope(e.target.value as 'products' | 'distributors')}
            aria-label="Search scope"
          >
            <option value="products">Products</option>
            <option value="distributors">Distributors</option>
          </select>
          <Search className="search-icon" size={18} />
          <input
            type="search"
            className="search-input"
            placeholder={
              searchScope === 'distributors'
                ? 'Search distributor name or domain…'
                : 'Search product name or code…'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>
      </div>

      <div className="header-right">
        <button className="header-icon-btn" title="Report an issue / Request support" onClick={onSupportClick} aria-label="Support">
          <LifeBuoy size={20} />
        </button>
        <button className="header-icon-btn" title="Help & User Guide" onClick={onHelpClick} aria-label="Help" data-tour="header-help">
          <HelpCircle size={20} />
        </button>
                <div className="header-status-group">
                <div className="auth-status-icon" title={authTitle}>
                  {authStatus === 'green' ? (
                    <ShieldCheck size={20} className="auth-ok" />
                  ) : authStatus === 'amber' ? (
                    <ShieldAlert size={20} className="auth-warn" />
                  ) : (
                    <ShieldX size={20} className="auth-fail" />
                  )}
                </div>
                <button
                  type="button"
                  className="health-status-icon header-icon-btn"
                  title="System health details"
                  aria-label="System health details"
                  onClick={() => setShowHealthDetails((prev) => !prev)}
                >
                  {dbHealthy === null || apiHealthy === null ? (
                    <HeartPulse size={20} className="health-pending" />
                  ) : dbHealthy && apiHealthy ? (
                    <HeartPulse size={20} className="health-ok" />
                  ) : (
                    <HeartCrack size={20} className="health-fail" />
                  )}
                </button>
                {showHealthDetails && (
                  <div className="health-details-popover" role="dialog" aria-label="System health details">
                    <div className="health-details-title">System Health</div>
                    <div className="health-details-row"><strong>API:</strong> {apiHealthy === null ? 'Checking...' : apiHealthy ? 'Healthy' : 'Unhealthy'}</div>
                    <div className="health-details-row"><strong>Database:</strong> {dbHealthy === null ? 'Checking...' : dbHealthy ? 'Healthy' : 'Unhealthy'}</div>
                    <div className="health-details-row"><strong>Authentication:</strong> {authStatus === 'green' ? 'SSO active' : authStatus === 'amber' ? 'Local login active' : 'Token invalid/expired'}</div>
                    <div className="health-details-row"><strong>Auth mode:</strong> {authMethod}</div>
                    <div className="health-details-row"><strong>Token:</strong> {tokenDiagnostics.reason}</div>
                    <div className="health-details-row"><strong>Last check:</strong> {checkedAt ? checkedAt.toLocaleTimeString() : 'Not yet'}</div>
                  </div>
                )}
                </div>
        <button className="header-icon-btn" title="Notifications">
          <Bell size={20} />
          {notificationCount > 0 && (
            <span className="badge notification-badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
          )}
        </button>

        <button
          type="button"
          className="header-icon-btn process-btn"
          title={processCount > 0 ? `${processCount} check(s) in progress` : 'Check history'}
          onClick={() => navigate('/check-history')}
        >
          {processCount > 0 ? (
            <>
              <Loader2 size={20} className="spinning" />
              <span className="badge process-badge">{processCount}</span>
            </>
          ) : (
            <Loader2 size={20} className="inactive" />
          )}
        </button>

        <div className="header-divider" />

        <button className="user-profile" onClick={onProfileClick}>
          <div className="user-info">
            <span className="user-name">{userName}</span>
            <span className="user-company">{userCompany}</span>
          </div>
          <div className="user-avatar">
            {userAvatar ? (
              <img src={userAvatar} alt={userName} />
            ) : (
              <span className="avatar-initials">{getInitials(userName)}</span>
            )}
          </div>
        </button>
      </div>
    </header>
  )
}
